import { defineStore, storeToRefs } from "pinia";
import { useToast } from "primevue/usetoast";
import { computed, ref, watch } from "vue";

import { useOrganizationManager } from "@/composables/organization-manager";
import { Artefact } from "@/lib/artefacts";
import { types, useServices } from "@/lib/openapi";
import { followLink } from "@/lib/openapi/helpers";
import {
    AuthStatus,
    useAuthenticationManager,
} from "@/stores/authentication-store";
import { onEntityChange } from "@/stores/cache-invalidation";

export type Stek = types.Stek;

// In-flight logo downloads keyed by `${stekId}:${logoArtefactId}` — shared so
// every caller (logo composable, stek manager, page watches) collapses into
// one request per logo.
const logoArtefactFetches = new Map<string, Promise<Artefact | undefined>>();

export const useSteksStore = defineStore("steks", () => {
    const toast = useToast();
    const isLoading = ref(false);
    const steks = ref<Stek[]>();
    const { organization } = useOrganizationManager();
    const { authStatus } = useAuthenticationManager();
    const api = useServices({
        requires: {
            auth: true,
            organization: true,
            stek: false,
        },
    });

    const controller = ref(new AbortController());

    const fetchSteks = async () => {
        if (
            authStatus.value !== AuthStatus.SIGNED_IN ||
            !organization.value?.identity
        ) {
            return;
        }

        controller.value.abort();
        controller.value = new AbortController();

        isLoading.value = true;
        try {
            const { response, data } =
                await api.startStekStekMgtGetSteksByOrganizationStartPost({
                    body: undefined,
                    signal: controller.value.signal,
                });

            if (response.ok) {
                steks.value = data?.sort((a, b) =>
                    a.name.localeCompare(b.name)
                );
            } else {
                if (response.status === 0) {
                    // Request was aborted by the AbortController
                    return;
                }
                throw new Error(
                    `Request failed with status ${response.status}`
                );
            }
        } catch (e) {
            if (e instanceof DOMException && e.name === "AbortError") {
                return;
            } else {
                throw e;
            }
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Fetch the logo for a given Stek.
     *
     * Concurrent calls for the same logo (page header + stek details both
     * request it on load) share one network request; once settled, a new
     * call fetches fresh again (so a just-uploaded logo still shows up).
     */
    const fetchLogo = async (stek: Stek): Promise<Artefact | undefined> => {
        if (!stek.logo) {
            return;
        }
        const fetchKey = `${stek.identity}:${stek.logo.identity}`;
        const inFlight = logoArtefactFetches.get(fetchKey);
        if (inFlight) {
            return inFlight;
        }
        const fetchPromise = fetchLogoUncached(stek).finally(() => {
            logoArtefactFetches.delete(fetchKey);
        });
        logoArtefactFetches.set(fetchKey, fetchPromise);
        return fetchPromise;
    };

    const fetchLogoUncached = async (
        stek: Stek
    ): Promise<Artefact | undefined> => {
        if (!stek.logo) {
            return;
        }

        const { response, data } = await followLink<File>(
            stek.logo,
            "get",
            undefined,
            undefined,
            undefined,
            {
                requires: {
                    auth: true,
                    organization: true,
                    stek: true,
                },
                overrides: {
                    stekId: stek.identity,
                },
            }
        );

        if (!response.ok) {
            if (response.status === 404) {
                // Not found is ok, we'll use the placeholder
                return;
            }
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        return new Artefact({
            name: "stekLogo",
            identity: stek.logo.identity,
            blob: data!,
            mimetype: data!.type,
            content: await data!.text(),
        });
    };

    // Cannot get steks if we aren't signed in.
    watch(
        authStatus,
        (newAuthStatus) => {
            if (newAuthStatus === AuthStatus.SIGNED_IN) {
                fetchSteks();
            }
        },
        { immediate: true }
    );

    watch(
        () => organization.value?.identity,
        async (identity, oldIdentity) => {
            if (identity) {
                if (oldIdentity) {
                    // Clear the previous org's list so stek resolution can't
                    // match against it while the new org's fetch is in flight.
                    steks.value = undefined;
                }
                await fetchSteks();
            } else {
                if (oldIdentity) {
                    steks.value = undefined;
                }
            }
        },
        // The second watcher triggers whenever the organization's identity changes.
        // If there is an identity, it fetches the steks for that organization.
        // If the identity becomes undefined (e.g., user switches orgs or logs out), it clears steks (if there was an org before).
        // `immediate: true` ensures the effect runs once right away on watcher setup.
        { immediate: true }
    );

    // Refetch the list whenever ANY Stek change is synced (logo, name, fqdn, …).
    // Views render stek.logo.preview / other fields straight off this list, and
    // a freshly-uploaded logo is a server-minted artefact we don't hold locally,
    // so a single targeted refetch (like create() does) is the correct refresh —
    // no per-attribute whitelist to forget. "stek" is the EntityType wire value,
    // matched as a literal to avoid a runtime dependency on the type-only `types`.
    onEntityChange(
        (change) => change.entityType === "stek",
        () => {
            fetchSteks();
        }
    );

    /**
     * Create a new Stek. Returns the created Stek, or the error body on
     * failure so the caller can show a specific reason (a toast is already
     * shown here for the generic case).
     */
    const create = async (
        name: string,
        fqdn?: string,
        logo?: File,
        opts: { showToast: boolean } = { showToast: true }
    ) => {
        isLoading.value = true;
        const { data, error, response } =
            await api.startStekStekMgtCreateStekStartPost({
                body: { name, fqdn, logo },
            });

        if (response.ok) {
            await fetchSteks();
        } else if (opts.showToast) {
            toast.add({
                severity: "error",
                summary: "Could not create stek",
                detail: "Server returned " + response.status,
            });
        }
        isLoading.value = false;
        return { data, error };
    };

    return {
        create,
        fetchSteks,
        steks,
        isLoading,
        fetchLogo,
    };
});

export const useSteksManager = () => storeToRefs(useSteksStore());

// Shared across every useSteksWithLogo() consumer: the data-url cache plus
// the in-flight fetches. Per-invocation state made each consumer (page +
// layout chrome) re-download the same logos in parallel.
const sharedLogos = ref<Map<string, string>>(new Map());
const logoFetches = new Map<string, Promise<void>>();

/**
 * Helper composable to provide steks with logos synchronously (but filled async).
 */
export function useSteksWithLogo() {
    const { fetchLogo } = useSteksStore();
    const { steks } = useSteksManager();
    const loading = ref(false);

    const steksWithLogos = computed(
        () =>
            steks.value?.map((stek) => ({
                stek,
                logoDataUrl: sharedLogos.value.get(stek.identity),
            })) || []
    );

    watch(
        steks,
        async () => {
            if (!steks.value?.length) return;

            loading.value = true;

            steks.value.map(async (stek) => {
                if (
                    sharedLogos.value.has(stek.identity) ||
                    logoFetches.has(stek.identity)
                ) {
                    return;
                }
                const fetchPromise = (async () => {
                    const logo = await fetchLogo(stek);
                    const dataUrl = await logo?.getDataUrl();
                    if (dataUrl) {
                        sharedLogos.value.set(stek.identity, dataUrl);
                    }
                })().finally(() => {
                    logoFetches.delete(stek.identity);
                });
                logoFetches.set(stek.identity, fetchPromise);
                await fetchPromise;
            });

            loading.value = false;
        },
        { immediate: true }
    );

    return { steksWithLogos, loading };
}
